import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline';
import get from 'lodash.get';
import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Pagination } from '../Pagination';
import { Text, TextType } from '../Text';
import { appendClassProps } from '../util';
import { SortDirection } from './index.types';
import { getColumnStyle } from './util';
const TableCell = ({ children, showRowLines = true, wrap, className, onClick }) => {
    // TODO: Conditionally apply title
    // https://codesandbox.io/s/material-demo-p2omr?file=/demo.js
    return (React.createElement("td", { className: `td${showRowLines ? ' border-t border-gray-300' : ''} ${onClick ? ' cursor-pointer' : ''}${wrap ? ' wrap' : ''}${appendClassProps(className)}`, onClick: onClick, title: typeof children === 'string' && !wrap ? children : undefined }, children));
};
/**
 - Table component for rendering a page of data
 - Can provide any array of data objects and the table will only render the columns provided in the column prop
 */
export const Table = ({ showHeader = true, showRowLines = true, highlightRows = true, className, columns, rows, pageNumber, countPerPage, totalCount, loading, onClickRow, onChangeCountPerPage, onChangePageNumber, onChangeColumns }) => {
    const handleChangeCountPerPage = (count) => {
        onChangeCountPerPage(parseInt(count));
    };
    const handleChangePageNumber = (page) => {
        onChangePageNumber(parseInt(page));
    };
    const handleClickColumn = (id) => {
        const newColumns = [...columns];
        for (let i = 0; i < newColumns.length; i++) {
            if (newColumns[i].id === id) {
                if (!newColumns[i].sortDirection) {
                    newColumns[i].sortDirection =
                        newColumns[i].sortDirOnFirstClick ?? SortDirection.up;
                }
                else {
                    newColumns[i].sortDirection =
                        newColumns[i].sortDirection === SortDirection.down
                            ? SortDirection.up
                            : SortDirection.down;
                }
            }
            else {
                delete newColumns[i].sortDirection;
            }
        }
        onChangeColumns(newColumns);
    };
    const tableBody = useMemo(() => rows.map((row, rowIdx) => (React.createElement("tr", { className: `${showRowLines ? 'border-b ' : ''} border-gray-300${onClickRow ? ' cursor-pointer' : ''}${highlightRows ? ' hover:bg-gray-100' : ''} relative`, key: rowIdx, onClick: () => {
            const highlightedText = window?.getSelection()?.toString();
            if (onClickRow && !highlightedText)
                onClickRow(row.id);
        } },
        columns.map((column, colIdx) => {
            let value = get(row, column.id);
            let content;
            if (typeof value === 'string' || typeof value === 'number') {
                content = (React.createElement(Text, { type: TextType.custom, className: "text-sm", key: column.id, wrap: column.wrap }, value));
            }
            else if (React.isValidElement(value)) {
                content = React.createElement(React.Fragment, { key: column.id }, value);
            }
            else {
                content = value ? (React.createElement(Text, { type: TextType.custom, className: "text-sm", key: column.id, wrap: column.wrap }, JSON.stringify(value))) : null;
            }
            return (React.createElement(TableCell, { className: `py-2 ${colIdx === 0 ? 'pl-4' : 'pl-2'} ${colIdx === columns.length - 1 ? 'pr-4' : 'pr-2'} relative${loading ? ' invisible' : ''}`, key: column.id, showRowLines: showRowLines && rowIdx !== 0, onClick: onClickRow
                    ? () => {
                        const highlightedText = window
                            ?.getSelection()
                            ?.toString();
                        if (onClickRow && !highlightedText)
                            onClickRow(row.id);
                    }
                    : undefined, wrap: column.wrap },
                React.createElement("div", { className: "h-full relative" }, content)));
        }),
        loading && (React.createElement("td", { className: "absolute top-0 left-0 right-0 bottom-0 z-0 px-4 py-2", colSpan: columns.length },
            React.createElement(Skeleton, { className: "h-full !leading-10", style: { zIndex: 0 } })))))), [rows, columns, showRowLines, loading, highlightRows]);
    let loadingTableBody = [];
    if (loading) {
        for (let i = 0; i < countPerPage; i++) {
            loadingTableBody.push(React.createElement("tr", { className: `border-b border-gray-300`, key: i },
                React.createElement("td", { className: "py-2 px-4 max-w-xs overflow-hidden whitespace-nowrap overflow-ellipsis", colSpan: columns.length },
                    React.createElement(Text, { type: TextType.custom, className: "text-sm", textClassName: "w-full" },
                        React.createElement(Skeleton, { className: "!leading-5", style: { zIndex: 0 } })))));
        }
    }
    return (React.createElement("table", { className: `relative w-full bg-gray-50 table-fixed${appendClassProps(className)}`, "data-testid": "table", cellPadding: "0", cellSpacing: "0" },
        React.createElement("colgroup", null, columns.map((col) => (React.createElement("col", { key: col.id, style: getColumnStyle(columns, col) })))),
        showHeader && (React.createElement("thead", { className: "sticky top-0 z-10" },
            React.createElement("tr", { className: "text-white bg-gray-400 font-extralight" }, columns.map((column) => (React.createElement("th", { className: "sticky top-0 p-2 text-white bg-gray-400 cursor-pointer", key: column.id, onClick: () => handleClickColumn(column.id) },
                React.createElement("div", { className: "flex flex-row gap-2" },
                    column.label && (React.createElement(Text, { type: TextType.custom, overrideColor: true, className: "text-sm", wrap: true }, column.label)),
                    column.sortDirection === SortDirection.up && (React.createElement(ChevronUpIcon, { className: "h-5" })),
                    column.sortDirection === SortDirection.down && (React.createElement(ChevronDownIcon, { className: "h-5" })),
                    !column.sortDirection && (React.createElement(ChevronDownIcon, { className: "h-5 opacity-0" }))))))))),
        React.createElement("tbody", null, loading && (!rows || rows.length < 1) ? loadingTableBody : tableBody),
        React.createElement("tfoot", null,
            React.createElement("tr", { className: "w-full" },
                React.createElement("td", { colSpan: columns.length, className: "sticky bottom-0 bg-gray-50 h-14" },
                    React.createElement(Pagination, { pageNumber: pageNumber, countPerPage: countPerPage, totalCount: totalCount, onChangeCountPerPage: handleChangeCountPerPage, onChangePageNumber: (page) => handleChangePageNumber(page.toString()), loading: loading }))))));
};
export * from './index.types';
//# sourceMappingURL=index.js.map